import { ComponentProps, useImperativeHandle, useMemo, useRef } from "react"

import { twMerge } from "tailwind-merge"

import { useFieldContext } from "./FieldProvider"
import { useAutosizeTextArea } from "~/src/hooks/useAutosizeTextArea"
import { PathBuilder } from "~/src/lib/iname"
import { dataFlag } from "~/src/lib/jsx"

export type TextAreaProps = { name?: PathBuilder | string; autoSize?: boolean; errored?: boolean } & Omit<
  ComponentProps<"textarea">,
  "name"
>

/**
 * A Beekit skin of the native textarea element that forwards its ref.
 */
export function TextArea(props: TextAreaProps) {
  const {
    name: name_,
    id: id_,
    errored: errored_,
    className,
    onChange,
    ref: ref_,
    autoSize = false,
    ...restProps
  } = props

  const ref = useRef<HTMLTextAreaElement>(null)
  const fieldContext = useFieldContext()
  const id = useMemo(() => id_ ?? fieldContext?.inputId, [id_, fieldContext?.inputId])
  const name = useMemo(() => name_?.toString() ?? fieldContext?.name, [name_, fieldContext?.name])
  const errored = useMemo(() => errored_ ?? fieldContext?.hasErrors, [errored_, fieldContext?.hasErrors])

  useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(ref_, () => ref?.current, [ref])
  useAutosizeTextArea(ref, autoSize)

  return (
    <textarea
      id={id}
      ref={ref}
      name={name?.toString()}
      onChange={(event) => {
        fieldContext?.descendentChange?.()
        onChange?.(event)
      }}
      className={twMerge(
        "rounded-lg border border-gray-300 disabled:cursor-not-allowed disabled:bg-gray-100",
        "data-errored:border-red-600 data-errored:focus:ring-red-600",
        autoSize && "resize-none",
        className
      )}
      {...dataFlag(errored, "errored")}
      {...restProps}
    />
  )
}
